import Head from 'next/head';

const FALLBACK_SITE_URL = 'https://kreatli.com';
const DEFAULT_OG_IMAGE_PATH = '/og-image.png';
const DEFAULT_OG_IMAGE_SIZE = { width: 1200, height: 630 };
const OG_SITE_NAME = 'Kreatli';

interface SeoHeadProps {
  title: string;
  description?: string;
  canonicalPath?: string;
  canonicalUrl?: string;
  ogType?: 'website' | 'article';
  imageUrl?: string;
  imageAlt?: string;
  /** Pixel size of `imageUrl`. Only emitted when known (the default OG image is 1200×630). */
  imageWidth?: number;
  imageHeight?: number;
  noindex?: boolean;
  /** With `noindex`: keep following the page's links ("noindex, follow"). For pages that stay in the nav. */
  followLinks?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
}

function getSiteUrl() {
  const envSiteUrl = 'https://kreatli.com';

  if (envSiteUrl) {
    return envSiteUrl;
  }

  return FALLBACK_SITE_URL;
}

function resolveAbsoluteUrl(url: string) {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  if (url.startsWith('//')) {
    return `https:${url}`;
  }

  const base = getSiteUrl();
  return `${base}${url.startsWith('/') ? url : `/${url}`}`;
}

function buildCanonicalUrl(canonicalPath?: string, canonicalUrl?: string) {
  if (canonicalUrl) {
    return resolveAbsoluteUrl(canonicalUrl);
  }

  if (!canonicalPath) {
    return undefined;
  }

  return resolveAbsoluteUrl(canonicalPath);
}

export function SeoHead({
  title,
  description,
  canonicalPath,
  canonicalUrl,
  ogType = 'website',
  imageUrl = DEFAULT_OG_IMAGE_PATH,
  imageAlt,
  imageWidth,
  imageHeight,
  noindex = false,
  followLinks = false,
  publishedTime,
  modifiedTime,
}: SeoHeadProps) {
  const resolvedCanonicalUrl = buildCanonicalUrl(canonicalPath, canonicalUrl);
  const resolvedImageAlt = imageAlt || title;
  const resolvedImageUrl = resolveAbsoluteUrl(imageUrl);
  const robots = noindex ? (followLinks ? 'noindex, follow' : 'noindex, nofollow') : undefined;
  const imageSize =
    imageWidth && imageHeight
      ? { width: imageWidth, height: imageHeight }
      : imageUrl === DEFAULT_OG_IMAGE_PATH
        ? DEFAULT_OG_IMAGE_SIZE
        : undefined;

  return (
    <Head>
      <title key="title">{title}</title>
      {description ? <meta key="description" name="description" content={description} /> : null}
      {resolvedCanonicalUrl ? <link key="canonical" rel="canonical" href={resolvedCanonicalUrl} /> : null}
      <meta key="og:site_name" property="og:site_name" content={OG_SITE_NAME} />
      <meta key="og:type" property="og:type" content={ogType} />
      <meta key="og:title" property="og:title" content={title} />
      {description ? <meta key="og:description" property="og:description" content={description} /> : null}
      {resolvedCanonicalUrl ? <meta key="og:url" property="og:url" content={resolvedCanonicalUrl} /> : null}
      <meta key="og:image" property="og:image" content={resolvedImageUrl} />
      <meta key="og:image:secure_url" property="og:image:secure_url" content={resolvedImageUrl} />
      <meta key="og:image:alt" property="og:image:alt" content={resolvedImageAlt} />
      {imageSize ? (
        <>
          <meta key="og:image:width" property="og:image:width" content={String(imageSize.width)} />
          <meta key="og:image:height" property="og:image:height" content={String(imageSize.height)} />
        </>
      ) : null}
      {publishedTime ? (
        <meta key="article:published_time" property="article:published_time" content={publishedTime} />
      ) : null}
      {modifiedTime ? (
        <meta key="article:modified_time" property="article:modified_time" content={modifiedTime} />
      ) : null}
      <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
      <meta key="twitter:title" name="twitter:title" content={title} />
      {description ? <meta key="twitter:description" name="twitter:description" content={description} /> : null}
      <meta key="twitter:image" name="twitter:image" content={resolvedImageUrl} />
      {robots ? <meta key="robots" name="robots" content={robots} /> : null}
    </Head>
  );
}

export const SITE_URL = FALLBACK_SITE_URL;
export const DEFAULT_OG_IMAGE = DEFAULT_OG_IMAGE_PATH;
